import { TitleCasePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Component, signal, computed, inject, DestroyRef, ElementRef, viewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { WorkshopService } from '@core/services/workshop';
import { Workshop } from '@core/models/workshop';
import { MdbModalRef, MdbModalService, MdbModalModule } from 'mdb-angular-ui-kit/modal';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { MdbTooltipModule } from 'mdb-angular-ui-kit/tooltip';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbTableModule } from 'mdb-angular-ui-kit/table';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';
import { MdbNotificationService } from 'mdb-angular-ui-kit/notification';
import { Gradientsegmentbar } from '@shared/components/gradientsegmentbar/gradientsegmentbar';
import { WorkshopModal } from '@shared/components/workshopmodal/workshopmodal';
import { Toast } from '@shared/components/toast/toast';
import {
  getWorkshopAriaLabel,
  getWorkshopButtonClass,
  getWorkshopButtonIcon,
  getWorkshopButtonText,
  getWorkshopModalMode,
  getWorkshopStatusClass,
  getWorkshopSeatBadgeClass,
  getFriendlyStatus,
  formatWorkshopDateTime,
  formatWorkshopTime,
  formatWorkshopMode,
  getWorkshopCardButtonClass,
  getRegisteredWorkshopStatusClass,
  formatWorkshopDateOnly
} from '@shared/utilities/workshop.util';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';

@Component({
  selector: 'app-my-workshops',
  imports: [
    TitleCasePipe,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MdbRippleModule,
    MdbTooltipModule,
    MdbFormsModule,
    MdbTableModule,
    MdbSelectModule,
    MdbCheckboxModule,
    MdbModalModule,
    Gradientsegmentbar
  ],
  providers: [MdbNotificationService],
  templateUrl: './my-workshops.html',
  styleUrl: './my-workshops.scss',
})
export class MyWorkshops {
  private destroyRef = inject(DestroyRef);
  private workshopService = inject(WorkshopService);
  private modalService = inject(MdbModalService);
  private notificationService = inject(MdbNotificationService);

  modalRef: MdbModalRef<WorkshopModal> | null = null;
  dataSource = signal<Workshop[]>([]);
  isLoading = signal(false);

  // Filters
  searchTerm = signal('');

  //
  isMobile = signal(window.innerWidth < 768);

  constructor() {
    this.loadWorkshops();
  }

  private loadWorkshops(): void {
    this.isLoading.set(true);
    this.workshopService.getRegisteredWorkshops()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.dataSource.set(data);
        this.isLoading.set(false);
      });
    //recalculate window width 
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.isMobile.set(window.innerWidth < 768);
      });
  }


  //#region Workshop Util
  protected getWorkshopStatusClass = getWorkshopStatusClass;
  protected getRegisteredWorkshopStatusClass = getRegisteredWorkshopStatusClass;
  protected getWorkshopSeatBadgeClass = getWorkshopSeatBadgeClass;
  protected getWorkshopButtonClass = getWorkshopButtonClass;
  protected getWorkshopCardButtonClass = getWorkshopCardButtonClass;
  protected getWorkshopButtonIcon = getWorkshopButtonIcon;
  protected getWorkshopButtonText = getWorkshopButtonText;
  protected getWorkshopAriaLabel = getWorkshopAriaLabel;
  protected getWorkshopFriendlyStatus = getFriendlyStatus;
  protected formatWorkshopDateTime = formatWorkshopDateTime;
  protected formatWorkshopDateOnly = formatWorkshopDateOnly;
  protected formatWorkshopTime = formatWorkshopTime;
  protected formatWorkshopMode = formatWorkshopMode;

  headers: { label: string; field: keyof Workshop }[] = [
    { label: 'Date', field: 'sessionStartTimestamp' },
    { label: 'Topic', field: 'topic' },
    { label: 'Workshop', field: 'title' },
    { label: 'Room', field: 'room' },
    { label: 'Format', field: 'mode' },
    { label: 'Language', field: 'language' },
    { label: 'Status', field: 'sessionStatus' },
    { label: 'Register/Cancel', field: 'workshopSessionId' }, // unsortable column, just needs a valid field
  ];

  //#endregion

  // Today and future, grouped by date for the top card
  upcomingWorkshops = computed<Workshop[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.dataSource().filter(w => new Date(w.sessionStartDateTime) >= today);
  });

  groupedWorkshops = computed<[string, Workshop[]][]>(() => {
    const grouped = new Map<string, Workshop[]>();
    this.upcomingWorkshops().forEach(workshop => {
      const key = workshop.sessionStartDateTime.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/Los_Angeles'
      });
      const existing = grouped.get(key) ?? [];
      grouped.set(key, [...existing, workshop]);
    });
    return Array.from(grouped.entries());
  });
  // Past workshops for the history table
  workshopHistory = computed<Workshop[]>(() => {
    const today = new Date();
    //console.log("today: " + today);
    return this.dataSource().filter(w => {
      const isPast = new Date(w.sessionEndDateTime) < today;
       //console.log(w);
      //TODO: Clarify cancelled session status AFTER demo
      const isCancelledOrPostponed = w.sessionStatus === 'Canceled' || w.sessionStatus === 'Postponed';
      return isPast || isCancelledOrPostponed;
    });
  });


  openModal(workshop: Workshop): void {
    const mode = getWorkshopModalMode(workshop);
    this.modalRef = this.modalService.open(WorkshopModal, {
      modalClass: 'modal-dialog-centered modal-xl modal-fullscreen-md-down',
      data: { workshop, mode }
    });

    this.modalRef.onClose.subscribe(() => {
      const result = this.modalRef?.component?.result;
      if (!result) return;

      this.notificationService.open(Toast, {
        data: {
          mode: result.success ? 'success' : 'error',
          message: result.message
        },
        position: 'top-center',
        autohide: true,
        delay: 5000
      });
    });
  }

  getWorkshopItemClass(workshop: Workshop): string {
    if (workshop.sessionStatus === 'Canceled' || workshop.sessionStatus === 'Postponed') return 'workshop-item workshop-cancelled';
    if (workshop.sessionStatus === 'Completed') return 'workshop-item workshop-completed';
    if (workshop.sessionStatus === 'Scheduled') return 'workshop-item workshop-completed';
    return 'workshop-item';
  }
}